import { Button } from "./ui/button";
import { ArrowRight, CheckCircle, TrendingUp, Users, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_venture-mentor-2/artifacts/52nzs5st_ChatGPT%20Image%20Jan%2022%2C%202026%2C%2002_22_56%20PM.png";
const EVANGELISTS_BG = "https://customer-assets.emergentagent.com/job_venture-mentor-2/artifacts/81bv0kg5_ChatGPT%20Image%20Jan%2027%2C%202026%2C%2002_16_37%20PM.png";

const LandingPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      // Not authenticated, that's fine
    }
  };

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/my-sessions';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="border-b border-[#E2E8F0] px-6 py-4 bg-white">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate("/")}>
            <img src={LOGO_URL} alt="9Mantra Logo" className="h-10 w-auto" />
            <h1 className="text-2xl font-normal text-[#1A1A1A]">9Mantra</h1>
          </div>
          <div className="flex items-center gap-4">
            {user ? (
              <>
                <Button
                  onClick={() => navigate("/my-sessions")}
                  variant="outline"
                  className="border-[#E2E8F0] text-[#1A1A1A]"
                >
                  My Sessions
                </Button>
                <Button
                  onClick={() => navigate("/validate")}
                  className="bg-[#0047AB] hover:bg-[#003580] text-white"
                >
                  New Validation
                </Button>
              </>
            ) : (
              <Button
                onClick={handleLogin}
                className="bg-[#0047AB] hover:bg-[#003580] text-white"
                data-testid="login-btn"
              >
                Sign In with Google
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Hero Section with Evangelist Background */}
      <section className="relative py-24 md:py-32 px-6 md:px-12 overflow-hidden">
        {/* Evangelists Background */}
        <div 
          className="absolute inset-0 opacity-[0.08] bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${EVANGELISTS_BG})`,
            backgroundSize: 'contain',
            backgroundPosition: 'center 60%'
          }}
        />
        
        {/* Content */}
        <div className="relative max-w-4xl mx-auto text-center z-10">
          <h1 
            className="text-5xl md:text-6xl lg:text-7xl font-normal text-[#1A1A1A] tracking-tight mb-6"
            data-testid="hero-heading"
          >
            AI-powered validation for your business idea.
          </h1>
          <p className="text-lg md:text-xl text-[#64748B] max-w-2xl mx-auto mb-12">
            Get your business idea evaluated by AI using 9 proven entrepreneurship philosophies. 
            From Warren Buffett's moats to Elon Musk's moonshots—AI analyzes it all.
          </p>
          <Button
            onClick={() => navigate("/validate")}
            className="bg-[#0047AB] hover:bg-[#003580] text-white px-8 py-6 text-lg rounded-md transition-all duration-300"
            data-testid="start-validation-btn"
          >
            Start Validation
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6 md:px-12 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl text-center text-[#1A1A1A] mb-16" data-testid="how-it-works-heading">
            How It Works
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white border border-[#E2E8F0] p-8" data-testid="step-1">
              <div className="w-12 h-12 bg-[#0047AB] text-white rounded-full flex items-center justify-center text-xl font-semibold mb-6">
                1
              </div>
              <h3 className="text-xl font-medium text-[#1A1A1A] mb-4">Submit Your Idea</h3>
              <p className="text-[#64748B]">
                Describe your business idea in detail (minimum 100 words). The more context, the better.
              </p>
            </div>

            <div className="bg-white border border-[#E2E8F0] p-8" data-testid="step-2">
              <div className="w-12 h-12 bg-[#0047AB] text-white rounded-full flex items-center justify-center text-xl font-semibold mb-6">
                2
              </div>
              <h3 className="text-xl font-medium text-[#1A1A1A] mb-4">AI Interview</h3>
              <p className="text-[#64748B]">
                Our AI asks up to 10 strategic questions to deeply understand your business model, market, and execution plan.
              </p>
            </div>

            <div className="bg-white border border-[#E2E8F0] p-8" data-testid="step-3">
              <div className="w-12 h-12 bg-[#0047AB] text-white rounded-full flex items-center justify-center text-xl font-semibold mb-6">
                3
              </div>
              <h3 className="text-xl font-medium text-[#1A1A1A] mb-4">AI-Powered Analysis</h3>
              <p className="text-[#64748B]">
                Get a comprehensive AI evaluation scored against 9 entrepreneurship mantras, plus AI-generated competitive insights and market research.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* The 9 Evangelists */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl md:text-4xl text-center text-[#1A1A1A] mb-6" data-testid="evangelists-heading">
            The 9 Entrepreneurship Evangelists
          </h2>
          <p className="text-center text-[#64748B] mb-16 max-w-2xl mx-auto">
            Your idea will be evaluated through the lens of these legendary thinkers.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { name: "Warren Buffett", mantra: "Is there a durable moat?" },
              { name: "Peter Thiel", mantra: "What valuable truth do few agree with?" },
              { name: "Jeff Bezos", mantra: "Is this customer-obsessed?" },
              { name: "Naval Ravikant", mantra: "Does this scale without your time?" },
              { name: "Reid Hoffman", mantra: "Does it work at scale?" },
              { name: "Marc Andreessen", mantra: "Is this market big enough?" },
              { name: "Elon Musk", mantra: "Does this move the future forward?" },
              { name: "Kishore Biyani", mantra: "How does this align with customer behaviour?" },
              { name: "Nikhil Kamath", mantra: "What's the fastest way this could fail?" }
            ].map((evangelist, idx) => (
              <div 
                key={idx} 
                className="bg-white border border-[#E2E8F0] p-6 hover:-translate-y-1 transition-all duration-300"
                data-testid={`evangelist-card-${idx}`}
              >
                <h3 className="text-lg font-medium text-[#1A1A1A] mb-2">{evangelist.name}</h3>
                <p className="text-sm text-[#64748B] italic">"{evangelist.mantra}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 md:px-12 bg-[#0047AB]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl text-white mb-6" data-testid="cta-heading">
            Ready to validate your idea with AI?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Get AI-driven insights in minutes. No fluff, just rigorous AI analysis based on proven frameworks.
          </p>
          <Button
            onClick={() => navigate("/validate")}
            className="bg-white text-[#0047AB] hover:bg-gray-100 px-8 py-6 text-lg rounded-md transition-all duration-300"
            data-testid="cta-button"
          >
            Start Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 text-center text-sm text-[#64748B] border-t border-[#E2E8F0]">
        <p>9Mantra - AI-Powered Business Validation. Wisdom, Systematized.</p>
      </footer>
    </div>
  );
};

export default LandingPage;